import { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import { createContent } from '@plone/volto/actions/content/content';
import FormattedRelativeDate from '@plone/volto/components/theme/FormattedDate/FormattedRelativeDate';
import Image from '@plone/volto/components/theme/Image/Image';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import filesize from 'filesize';
import { validateAltText } from 'govbr.frontend.policy/helpers/validations';
import concat from 'lodash/concat';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { readAsDataURL } from 'promise-file-reader';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Accordion,
  Button,
  Dimmer,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Modal,
  Progress,
  Segment,
  Table,
} from 'semantic-ui-react';

const Dropzone = loadable(() => import('react-dropzone'));

const messages = defineMessages({
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  upload: {
    id: '{count, plural, one {Upload {count} file} other {Upload {count} files}}',
    defaultMessage:
      '{count, plural, one {Upload {count} file} other {Upload {count} files}}',
  },
  filesUploaded: {
    id: 'Files uploaded: {uploadedFiles}',
    defaultMessage: 'Files uploaded: {uploadedFiles}',
  },
  totalFilesToUpload: {
    id: 'Total files to upload: {totalFiles}',
    defaultMessage: 'Total files to upload: {totalFiles}',
  },
  images: {
    id: 'Images',
    defaultMessage: 'Images',
  },
  otherFiles: {
    id: 'Other files',
    defaultMessage: 'Other files',
  },
  description: {
    id: 'imageDescriptionLabel',
    defaultMessage: 'Description',
  },
  removeFile: {
    id: 'Remove file',
    defaultMessage: 'Remove file',
  },
  altPlaceholder: {
    id: 'Alt placeholder',
    defaultMessage: 'Describe the image for assistive technologies',
  },
  captionPlaceholder: {
    id: 'Caption placeholder',
    defaultMessage: 'Caption displayed below the image',
  },
  descriptionPlaceholder: {
    id: 'Description placeholder',
    defaultMessage: 'Optional internal image description',
  },
  altLabel: {
    id: 'imageAltLabel',
    defaultMessage: 'Alternative Text - Alt (required)*',
  },
  captionLabel: {
    id: 'imageCaptionLabel',
    defaultMessage: 'Caption',
  },
  validationErrorToast: {
    id: 'imageBatchValidationError',
    defaultMessage:
      'There are errors in the image data. Review the highlighted fields.',
  },
});

const SUBREQUEST = 'batch-upload';

const isImageFile = (file) => (file?.type || '').startsWith('image/');
const getLastModifiedDate = (file) =>
  file?.lastModifiedDate ||
  (file?.lastModified ? new Date(file.lastModified) : null);

const ContentsUploadModal = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const request = useSelector(
    (state) => state.content.subrequests?.[SUBREQUEST] || {},
    shallowEqual,
  );
  const uploadedFiles = useSelector((state) => state.content.uploadedFiles);

  const prevrequestloading = usePrevious(request.loading);
  const [files, setFiles] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);

  // Metadados por arquivo (somente para imagens)
  const [meta, setMeta] = useState([]);
  const [errors, setErrors] = useState([]);
  const [openImageIds, setOpenImageIds] = useState([]);
  const altInputRefs = useRef({});

  const { onOk } = props;
  useEffect(() => {
    if (prevrequestloading && request.loaded) {
      onOk();
      setFiles([]);
      setMeta([]);
      setErrors([]);
      setOpenImageIds([]);
      setTotalFiles(0);
    }
  }, [prevrequestloading, request.loaded, onOk]);

  useEffect(() => {
    // Garante que novas imagens comecem abertas, preservando as que o usuário fechou
    setOpenImageIds((prev) => {
      const prevSet = new Set(prev);
      const imageFiles = files.filter((f) => isImageFile(f));
      let changed = false;
      const next = [...prev];

      imageFiles.forEach((file, index) => {
        const id = file.__uploadId || index;
        if (!prevSet.has(id)) {
          next.push(id);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [files]);

  const onRemoveFile = (event) => {
    const removeIndex = Number(event.currentTarget.getAttribute('value'));
    setFiles((prevFiles) => {
      const newFiles = filter(
        prevFiles,
        (_file, index) => index !== removeIndex,
      );
      setTotalFiles(newFiles.length);
      return newFiles;
    });
    setMeta((prevMeta) =>
      filter(prevMeta, (_m, index) => index !== removeIndex),
    );
    setErrors((prevErrors) =>
      filter(prevErrors, (_m, index) => index !== removeIndex),
    );
  };

  const onDrop = async (newFiles) => {
    const validFiles = [];
    const newMeta = [];
    const newErrors = [];

    for (const file of newFiles) {
      if (validateFileUploadSize(file, intl.formatMessage)) {
        const data = await readAsDataURL(file);
        const fields = data.match(/^data:(.*);(.*),(.*)$/);
        // eslint-disable-next-line no-param-reassign
        file.preview = fields[0];
        // eslint-disable-next-line no-param-reassign
        file.__uploadId = file.__uploadId || `${Date.now()}-${Math.random()}`;

        validFiles.push(file);

        const image = isImageFile(file);
        newMeta.push({ alt: '', caption: '', description: '' });
        newErrors.push({
          alt: image ? null : null,
          caption: image ? null : null,
        });
      }
    }

    if (!validFiles.length) return;

    setFiles((prevFiles) => {
      const merged = concat(prevFiles, validFiles);
      setTotalFiles(merged.length);
      return merged;
    });
    setMeta((prevMeta) => concat(prevMeta, newMeta));
    setErrors((prevErrors) => concat(prevErrors, newErrors));
  };

  const onCancel = () => {
    props.onCancel();
    setFiles([]);
    setMeta([]);
    setErrors([]);
    setTotalFiles(0);
    setOpenImageIds([]);
  };

  const onChangeFileName = (e, index) => {
    let copyOfFiles = [...files];
    let originalFile = files[index];
    let newFile = new File([originalFile], e.target.value, {
      type: originalFile.type,
    });
    newFile.preview = originalFile.preview;
    newFile.path = e.target.value;
    copyOfFiles[index] = newFile;
    setFiles(copyOfFiles);
  };

  const updateMeta = (index, field, value) => {
    setMeta((prevMeta) => {
      const updatedMeta = [...prevMeta];
      updatedMeta[index] = { ...updatedMeta[index], [field]: value };
      return updatedMeta;
    });

    const isImage = isImageFile(files[index]);
    if (!isImage) return;

    setErrors((prevErrors) => {
      const updatedErrors = [...prevErrors];
      const current = updatedErrors[index] || {};

      if (field === 'alt') {
        const err = validateAltText(value);
        updatedErrors[index] = {
          ...current,
          alt: err ? err : null,
        };
      }
      if (field === 'caption') {
        const trimmed = (value || '').trim();
        let err = null;
        if (trimmed.length > 500)
          err = intl.formatMessage({
            id: 'Maximum 500 characters',
            defaultMessage: 'Maximum 500 characters',
          });
        updatedErrors[index] = {
          ...current,
          caption: err,
        };
      }

      return updatedErrors;
    });
  };

  const toggleImageAccordion = (id) => {
    setOpenImageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isUploadDisabled = () => {
    // Só desabilita o botão enquanto estiver carregando
    return request.loading;
  };

  const validateBeforeSubmit = () => {
    const newErrors = [...errors];
    let firstErrorImageIndex = null;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!isImageFile(file)) continue;

      const metaData = meta[i] || {};
      const altValue = metaData.alt || '';
      const captionValue = (metaData.caption || '').trim();

      const altError = validateAltText(altValue);
      let captionError = null;
      if (captionValue.length > 500) {
        captionError = intl.formatMessage({
          id: 'Maximum 500 characters',
          defaultMessage: 'Maximum 500 characters',
        });
      }

      if (altError || captionError) {
        newErrors[i] = {
          ...(newErrors[i] || {}),
          alt: altError,
          caption: captionError,
        };
        if (firstErrorImageIndex === null) {
          firstErrorImageIndex = i;
        }
      }
    }

    if (firstErrorImageIndex !== null) {
      setErrors(newErrors);

      const fileWithError = files[firstErrorImageIndex];
      const id = fileWithError.__uploadId || firstErrorImageIndex;

      setOpenImageIds((prev) => (prev.includes(id) ? prev : [...prev, id]));

      // toast de erro global
      toast.error(intl.formatMessage(messages.validationErrorToast));

      return false;
    }

    return true;
  };

  const onSubmit = () => {
    if (!validateBeforeSubmit()) return;

    Promise.all(map(files, (file) => readAsDataURL(file))).then((dataUrls) => {
      dispatch(
        createContent(
          props.pathname,
          map(files, (file, index) => {
            const fields = dataUrls[index].match(/^data:(.*);(.*),(.*)$/);
            const image = fields[1].split('/')[0] === 'image';
            const metaData = meta[index] || {};
            const base = {
              '@type': image ? 'Image' : 'File',
              title: file.name,
              [image ? 'image' : 'file']: {
                data: fields[3],
                encoding: fields[2],
                'content-type': fields[1],
                filename: file.name,
              },
            };
            if (image) {
              if (metaData.alt) base.alt = metaData.alt;
              if (metaData.caption) base.caption = metaData.caption;
              if (metaData.description) base.description = metaData.description;
            }
            return base;
          }),
          SUBREQUEST,
        ),
      );
    });
  };

  const {
    multiple = true,
    minSize = null,
    maxSize = null,
    accept = null,
    disabled = false,
  } = props;
  const dropzoneOptions = {
    multiple,
    minSize,
    maxSize,
    accept,
    disabled,
  };

  return (
    props.open && (
      <Modal className="contents-upload-modal" open={props.open}>
        <Header>
          <FormattedMessage id="Upload files" defaultMessage="Upload files" />
        </Header>
        <Dimmer active={request.loading}>
          <div className="progress-container">
            <Progress
              className="progress-bar"
              value={uploadedFiles}
              total={totalFiles}
            >
              {intl.formatMessage(messages.filesUploaded, {
                uploadedFiles,
              })}
              <br />
              {intl.formatMessage(messages.totalFilesToUpload, {
                totalFiles,
              })}
            </Progress>
          </div>
        </Dimmer>
        <Modal.Content>
          <Dropzone
            onDrop={onDrop}
            className="dropzone"
            noDragEventsBubbling={true}
            {...dropzoneOptions}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                {...getRootProps({ className: 'dashed' })}
                aria-label={intl.formatMessage({
                  id: 'Upload area',
                  defaultMessage:
                    'Area to drag and drop files or select using the Browse button',
                })}
              >
                <Segment>
                  <Table basic="very">
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>
                          <FormattedMessage
                            id="Drag and drop files from your computer onto this area or click the “Browse” button."
                            defaultMessage="Drag and drop files from your computer onto this area or click the “Browse” button."
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Button className="ui button primary">
                            <FormattedMessage
                              id="Browse"
                              defaultMessage="Browse"
                            />
                          </Button>
                          <input
                            {...getInputProps({
                              type: 'file',
                              style: { display: 'none' },
                            })}
                          />
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </Segment>
              </div>
            )}
          </Dropzone>
          {files.length > 0 && (
            <>
              {/* Seção de Imagens */}
              {files.some((f) => isImageFile(f)) && (
                <>
                  <Header as="h4">
                    <FormattedMessage {...messages.images} />
                  </Header>
                  <Accordion fluid styled className="images-accordion">
                    {map(
                      files.filter((f) => isImageFile(f)),
                      (file) => {
                        const index = files.findIndex((f) => f === file);
                        const lastModified = getLastModifiedDate(file);
                        const id = file.__uploadId || index;
                        const isOpen = openImageIds.includes(id);

                        return (
                          <div key={id} className="images-accordion-item">
                            <Accordion.Title
                              active={isOpen}
                              onClick={() => toggleImageAccordion(id)}
                              className="images-accordion-title"
                            >
                              <Icon name="dropdown" />
                              {file.name}
                            </Accordion.Title>
                            <Accordion.Content active={isOpen}>
                              <Segment className="upload-card" padded="very">
                                <Grid stackable>
                                  <Grid.Row columns={2}>
                                    <Grid.Column
                                      mobile={16}
                                      tablet={5}
                                      computer={4}
                                    >
                                      <div className="upload-card-preview">
                                        <Image
                                          src={file.preview}
                                          height={100}
                                          className="ui image"
                                        />
                                      </div>
                                      <div className="upload-card-fileinfo">
                                        {lastModified && (
                                          <div className="file-last-modified">
                                            <FormattedMessage
                                              id="Last modified"
                                              defaultMessage="Last modified"
                                            />
                                            :{' '}
                                            <FormattedRelativeDate
                                              date={lastModified}
                                            />
                                          </div>
                                        )}
                                        <div className="file-size">
                                          <FormattedMessage
                                            id="File size"
                                            defaultMessage="File size"
                                          />
                                          : {filesize(file.size, { round: 0 })}
                                        </div>
                                      </div>
                                    </Grid.Column>
                                    <Grid.Column
                                      mobile={16}
                                      tablet={11}
                                      computer={12}
                                    >
                                      <Form className="mt-3">
                                        <Form.Input
                                          label={
                                            <FormattedMessage
                                              id="Filename"
                                              defaultMessage="Filename"
                                            />
                                          }
                                          value={file.name}
                                          onChange={(e) =>
                                            onChangeFileName(e, index)
                                          }
                                          maxLength={160}
                                        />
                                        <Form.Input
                                          label={
                                            <FormattedMessage
                                              {...messages.altLabel}
                                            />
                                          }
                                          value={meta[index]?.alt || ''}
                                          onChange={(ev, { value }) =>
                                            updateMeta(index, 'alt', value)
                                          }
                                          placeholder={intl.formatMessage(
                                            messages.altPlaceholder,
                                          )}
                                          error={
                                            errors[index]?.alt
                                              ? {
                                                  content: errors[index].alt,
                                                  pointing: 'below',
                                                }
                                              : null
                                          }
                                          maxLength={160}
                                          inputRef={(node) => {
                                            if (node) {
                                              altInputRefs.current[id] = node;
                                            }
                                          }}
                                        />
                                        <Form.TextArea
                                          label={
                                            <FormattedMessage
                                              {...messages.captionLabel}
                                            />
                                          }
                                          value={meta[index]?.caption || ''}
                                          onChange={(ev, { value }) =>
                                            updateMeta(index, 'caption', value)
                                          }
                                          placeholder={intl.formatMessage(
                                            messages.captionPlaceholder,
                                          )}
                                          error={
                                            errors[index]?.caption
                                              ? {
                                                  content:
                                                    errors[index].caption,
                                                  pointing: 'below',
                                                }
                                              : null
                                          }
                                          maxLength={500}
                                          rows={3}
                                        />
                                        <Form.TextArea
                                          label={
                                            <FormattedMessage
                                              {...messages.description}
                                            />
                                          }
                                          value={meta[index]?.description || ''}
                                          onChange={(ev, { value }) =>
                                            updateMeta(
                                              index,
                                              'description',
                                              value,
                                            )
                                          }
                                          placeholder={intl.formatMessage(
                                            messages.descriptionPlaceholder,
                                          )}
                                          maxLength={500}
                                          rows={3}
                                        />
                                        <div className="upload-card-actions">
                                          <Button
                                            size="small"
                                            icon="trash"
                                            floated="right"
                                            aria-label={intl.formatMessage(
                                              messages.removeFile,
                                            )}
                                            title={intl.formatMessage(
                                              messages.removeFile,
                                            )}
                                            onClick={onRemoveFile}
                                            value={index}
                                          />
                                        </div>
                                      </Form>
                                    </Grid.Column>
                                  </Grid.Row>
                                </Grid>
                              </Segment>
                            </Accordion.Content>
                          </div>
                        );
                      },
                    )}
                  </Accordion>
                </>
              )}

              {/* Seção de Outros arquivos */}
              {files.some((f) => !isImageFile(f)) && (
                <>
                  <Header as="h4">
                    <FormattedMessage {...messages.otherFiles} />
                  </Header>
                  <Table compact singleLine stackable>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={8}>
                          <FormattedMessage
                            id="Filename"
                            defaultMessage="Filename"
                          />
                        </Table.HeaderCell>
                        <Table.HeaderCell width={4}>
                          <FormattedMessage
                            id="Last modified"
                            defaultMessage="Last modified"
                          />
                        </Table.HeaderCell>
                        <Table.HeaderCell width={4}>
                          <FormattedMessage
                            id="File size"
                            defaultMessage="File size"
                          />
                        </Table.HeaderCell>
                        <Table.HeaderCell width={4}>
                          <FormattedMessage
                            id="Preview"
                            defaultMessage="Preview"
                          />
                        </Table.HeaderCell>
                        <Table.HeaderCell />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {map(
                        files.filter((f) => !isImageFile(f)),
                        (file) => {
                          const index = files.findIndex((f) => f === file);
                          return (
                            <Table.Row className="upload-row" key={index}>
                              <Table.Cell>
                                <Input
                                  className="file-name"
                                  value={file.name}
                                  onChange={(e) => onChangeFileName(e, index)}
                                />
                              </Table.Cell>
                              <Table.Cell>
                                {file.lastModifiedDate && (
                                  <FormattedRelativeDate
                                    date={file.lastModifiedDate}
                                  />
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                {filesize(file.size, { round: 0 })}
                              </Table.Cell>
                              <Table.Cell>
                                {file.type.split('/')[0] === 'image' && (
                                  <Image
                                    src={file.preview}
                                    height={60}
                                    className="ui image"
                                  />
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                <Icon
                                  name="close"
                                  value={index}
                                  link
                                  onClick={onRemoveFile}
                                />
                              </Table.Cell>
                            </Table.Row>
                          );
                        },
                      )}
                    </Table.Body>
                  </Table>
                </>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          {files.length > 0 && (
            <Button
              basic
              circular
              primary
              floated="right"
              icon="arrow right"
              aria-label={intl.formatMessage(messages.upload, {
                count: files.length,
              })}
              onClick={onSubmit}
              title={intl.formatMessage(messages.upload, {
                count: files.length,
              })}
              size="big"
              disabled={isUploadDisabled()}
            />
          )}
          <Button
            basic
            circular
            secondary
            icon="remove"
            aria-label={intl.formatMessage(messages.cancel)}
            title={intl.formatMessage(messages.cancel)}
            floated="right"
            size="big"
            onClick={onCancel}
          />
        </Modal.Actions>
      </Modal>
    )
  );
};

ContentsUploadModal.propTypes = {
  pathname: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  minSize: PropTypes.number,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};
export default ContentsUploadModal;
